import { SubmissionError } from '#helper/submission-error';
import {
  WAREHOUSE_FETCH_STARTED,
  WAREHOUSE_FETCH_SUCCEEDED,
  WAREHOUSE_FETCH_FAILED,
  WAREHOUSE_CLEAR,
  SUCCESS
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { snackBarError } from '#action/fetch';
import { fetchProject } from '#action/project/dashboard';
import { getRequestOptions } from '#helper';
import { history } from '#configureStore';

let abortController = new AbortController();

export const fetchWarehouse = (id, withProject) => async dispatch => {
  try {
    abortController.abort();
    const warehouseUrl = webPortalApi.buildApiUrl(`warehouse/${id}`);
    dispatch({
      type: WAREHOUSE_FETCH_STARTED
    });
    abortController = new AbortController();
    const { signal } = abortController;
    const res = await fetch(warehouseUrl, { ...getRequestOptions(), signal });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    const warehouse = await res.json();

    if (!res.ok) {
      const shallRedirect = [403, 404].includes(warehouse.resStatus);

      dispatch(
        snackBarError({
          message: 'Error in request',
          ...warehouse
        })
      );

      if (shallRedirect) {
        await history.push('/warehouse');
      }

      throw warehouse;
    }

    if (withProject && warehouse.project?.id) {
      const project = await fetchProject(warehouse.project?.id);

      dispatch({
        type: WAREHOUSE_FETCH_SUCCEEDED,
        payload: {
          ...warehouse,
          project
        }
      });

      return;
    }

    dispatch({
      type: WAREHOUSE_FETCH_SUCCEEDED,
      payload: warehouse
    });
  } catch (e) {
    if (e.name === 'AbortError') {
      return;
    }
    dispatch({
      type: WAREHOUSE_FETCH_FAILED,
      error: e
    });
  }
};

export const clear = () => dispatch => {
  abortController.abort();
  dispatch({
    type: WAREHOUSE_CLEAR
  });
};

export const createWarehouse = warehouse => async dispatch => {
  try {
    const warehouseUrl = webPortalApi.buildApiUrl('warehouse');
    const res = await fetch(warehouseUrl, {
      method: 'POST',
      body: JSON.stringify(warehouse),
      ...getRequestOptions()
    });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      throw await res.json();
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Site has been created'
      }
    });

    return await res.json();
  } catch (e) {
    dispatch(snackBarError({
      message: 'Error in request',
      ...e
    }));
    throw new SubmissionError(e);
  }
};

export const updateWarehouse = (warehouseId, warehouse) => async dispatch => {
  try {
    const warehouseUrl = webPortalApi.buildApiUrl(`warehouse/${warehouseId}`);
    const res = await fetch(warehouseUrl, {
      method: 'PATCH',
      body: JSON.stringify(warehouse),
      ...getRequestOptions()
    });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      throw await res.json();
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Site has been updated'
      }
    });

    return await res.json();
  } catch (e) {
    dispatch(snackBarError({
      message: 'Error in request',
      ...e
    }));
    throw new SubmissionError(e);
  }
};

export const deleteWarehouse = warehouseId => async dispatch => {
  try {
    const warehouseUrl = webPortalApi.buildApiUrl(`warehouse/${warehouseId}`);
    const res = await fetch(warehouseUrl, {
      method: 'DELETE',
      ...getRequestOptions()
    });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      throw await res.json();
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Site has been deleted'
      }
    });

    return await res.json();
  } catch (e) {
    dispatch(snackBarError({
      message: 'Error in request',
      ...e
    }));
    return null;
  }
};
